/**
 * AppErrorBoundary — wraps the React tree, reports renders crashes to Sentry,
 * and shows a friendly fallback that never blocks user from rolling forward.
 *
 * Brand-aligned (slate-950 / emerald accents). When DSN is unset, Sentry's
 * ErrorBoundary still works as a pure React error boundary — it just doesn't
 * report anywhere.
 */
import React from 'react';
import { Sentry } from '../lib/sentryInit';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';

const Fallback = ({ error, resetError }) => (
  <div
    className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 p-6"
    data-testid="error-boundary-fallback"
  >
    <div className="max-w-md w-full bg-slate-900 border border-amber-500/30 rounded-xl p-8 text-center">
      <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4">
        <AlertTriangle className="w-6 h-6 text-amber-400" />
      </div>
      <h1 className="text-2xl font-bold text-white mb-2">Something just broke.</h1>
      <p className="text-slate-400 text-sm mb-6">
        We've been notified and are looking into it. You can try again or head home.
        If it keeps happening, email{' '}
        <a href="mailto:hello@cartieai.com" className="text-emerald-300 hover:text-emerald-200">
          hello@cartieai.com
        </a>.
      </p>
      {process.env.NODE_ENV !== 'production' && error?.message && (
        <pre className="text-xs text-rose-300 bg-slate-950 border border-rose-500/20 rounded-md p-3 mb-4 text-left overflow-auto max-h-40">
          {String(error.message)}
        </pre>
      )}
      <div className="flex gap-2 justify-center">
        <Button
          onClick={resetError}
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
          data-testid="error-boundary-retry"
        >
          <RefreshCcw className="w-4 h-4 mr-1.5" /> Try again
        </Button>
        <Button
          variant="outline"
          onClick={() => { window.location.href = '/'; }}
          className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10"
          data-testid="error-boundary-home"
        >
          <Home className="w-4 h-4 mr-1.5" /> Home
        </Button>
      </div>
    </div>
  </div>
);

const AppErrorBoundary = ({ children }) => (
  <Sentry.ErrorBoundary
    fallback={(props) => <Fallback {...props} />}
    showDialog={false}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default AppErrorBoundary;
